import { useEffect, useRef, useState, useCallback } from "react";
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc, serverTimestamp, collection } from "firebase/firestore";
import { storage, db } from "../firebase";
import { invalidatePatternTierCache } from "./usePatternTier";
import { authedWrite } from "../utils/authedWrite";
import {
  getAllPendingVideos,
  removePendingVideo,
  getAllPendingCatches,
  removePendingCatch,
  getAllPendingGeofenceAlerts,
  removePendingGeofenceAlert,
  getPendingVideoCount,
  getPendingCatchCount,
  getPendingGeofenceAlertCount,
  QUEUE_CHANGED_EVENT,
} from "../services/offlineStore";
import { getFileBlob, deleteFile as deleteStagedFile } from "../services/videoFileStore";
import { sendTournamentGeofenceAlert } from "../services/tournamentAlerts";
import { sendDirectorCertifiedVideoEvent } from "../services/tournamentEventWriters";
import { isR2Backend } from "../config/videoBackend";
import { uploadVideo as r2UploadVideo, getPlaybackUrl as r2GetPlaybackUrl } from "../services/r2Video";

/**
 * Best-effort detection of a metered/cellular connection so we can honour the
 * "upload over cellular" preference. Uses the Network Information API where
 * available; when it can't tell, returns false (assume uploading is allowed).
 */
function isOnCellular() {
  try {
    const conn =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.type) return conn.type === "cellular";
    // effectiveType is present without `type` on many Android WebViews; it does
    // not distinguish wifi vs cellular, so fall back to the `saveData`/metered
    // hint only when explicitly set.
    return conn.type === "cellular";
  } catch {
    return false;
  }
}

/**
 * Watches network status and, when online, drains the IndexedDB queues:
 *   - pendingVideos  → uploads to Firebase Storage + writes Firestore metadata
 *   - pendingCatches → writes to Firestore (as a safety net; Firestore SDK
 *                      already queues these itself, but we keep an explicit
 *                      backup for forced offline mode)
 *
 * Returns { pendingVideoCount, pendingCatchCount, syncing, lastSyncAt }
 *
 * `uploadSettings` gates auto-upload behaviour (persisted in Firestore):
 *   { autoUpload, uploadOverCellular, deleteAfterUpload }
 */
export default function useOfflineSync({ isOffline, uploadSettings }) {
  const [pendingVideoCount, setPendingVideoCount] = useState(0);
  const [pendingCatchCount, setPendingCatchCount] = useState(0);
  const [pendingGeofenceAlertCount, setPendingGeofenceAlertCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [items, setItems] = useState([]);
  const [activeUpload, setActiveUpload] = useState(null);
  const syncingRef = useRef(false);
  const prevIsOfflineRef = useRef(isOffline);
  const attemptsRef = useRef(new Map());
  const MAX_ATTEMPTS = 5;

  // Latest upload settings, read without re-creating syncAll on every change.
  const settingsRef = useRef(uploadSettings || {});
  useEffect(() => {
    settingsRef.current = uploadSettings || {};
  }, [uploadSettings]);

  const refreshItems = useCallback(async () => {
    try {
      const [videos, catches, alerts] = await Promise.all([
        getAllPendingVideos(),
        getAllPendingCatches(),
        getAllPendingGeofenceAlerts(),
      ]);
      const mapped = [
        ...videos.map((v) => {
          const key = `video:${v.id}`;
          const attempts = attemptsRef.current.get(key) || 0;
          const c = v.metadata?.videoMeta?.catch || v.metadata?.certificateMeta?.catch || {};
          const species = c.species;
          const gps = v.metadata?.videoMeta?.gps || v.metadata?.certificateMeta?.gps || null;
          const parts = [];
          if (c.weightLbs != null) parts.push(`${c.weightLbs} lb`);
          if (c.lengthIn != null) parts.push(`${c.lengthIn} in`);
          if (c.lure) parts.push(c.lure);
          return {
            key,
            id: v.id,
            type: "video",
            label: species ? `Video · ${species}` : "Catch video",
            title: species || "Catch video",
            subtitle: parts.join(" · ") || (v.metadata?.videoMeta?.source === "gopro" ? "GoPro clip" : "Video clip"),
            location: c.location || (gps ? `${gps.lat?.toFixed?.(4)}, ${gps.lng?.toFixed?.(4)}` : null),
            hash: v.metadata?.hash || null,
            sizeBytes: v.blob?.size || v.fileSize || 0,
            onDevice: !v.blob && !!v.filePath,
            filePath: v.filePath || null,
            queuedAt: v.queuedAt || 0,
            attempts,
            status: attempts >= MAX_ATTEMPTS ? "failed" : "queued",
          };
        }),
        ...catches.map((c) => {
          const key = `catch:${c.id}`;
          const attempts = attemptsRef.current.get(key) || 0;
          const cd = c.catchData || {};
          const parts = [];
          if (cd.weightLbs != null) parts.push(`${cd.weightLbs} lb`);
          if (cd.lengthIn != null) parts.push(`${cd.lengthIn} in`);
          if (cd.lure) parts.push(cd.lure);
          return {
            key,
            id: c.id,
            type: "catch",
            label: cd.species ? `Catch · ${cd.species}` : "Catch log",
            title: cd.species || "Catch log",
            subtitle: parts.join(" · ") || "Certified catch",
            location: c.location ? `${c.location.lat?.toFixed?.(4)}, ${c.location.lng?.toFixed?.(4)}` : null,
            hash: null,
            sizeBytes: 0,
            queuedAt: c.queuedAt || 0,
            attempts,
            status: attempts >= MAX_ATTEMPTS ? "failed" : "queued",
          };
        }),
        ...alerts.map((a) => {
          const key = `alert:${a.id}`;
          const attempts = attemptsRef.current.get(key) || 0;
          return {
            key,
            id: a.id,
            type: "alert",
            label: "Tournament boundary alert",
            title: "Boundary alert",
            subtitle: a.tournamentName || "Tournament geofence",
            location: a.location ? `${a.location.lat?.toFixed?.(4)}, ${a.location.lng?.toFixed?.(4)}` : null,
            hash: null,
            sizeBytes: 0,
            queuedAt: a.queuedAt || 0,
            attempts,
            status: attempts >= MAX_ATTEMPTS ? "failed" : "queued",
          };
        }),
      ].sort((a, b) => (a.queuedAt || 0) - (b.queuedAt || 0));
      setItems(mapped);
    } catch {
      /* best-effort */
    }
  }, []);

  const refreshCounts = useCallback(async () => {
    try {
      const [vc, cc, gc] = await Promise.all([
        getPendingVideoCount(),
        getPendingCatchCount(),
        getPendingGeofenceAlertCount(),
      ]);
      setPendingVideoCount(vc);
      setPendingCatchCount(cc);
      setPendingGeofenceAlertCount(gc);
      refreshItems();
      return { vc, cc, gc };
    } catch {
      return { vc: 0, cc: 0, gc: 0 };
    }
  }, [refreshItems]);

  const syncAll = useCallback(async (force = false) => {
    if (syncingRef.current) return;

    // Respect the "upload over cellular" preference for automatic syncs. A
    // manual "Upload now" (force) always proceeds — the user opted in.
    if (!force && settingsRef.current.uploadOverCellular === false && isOnCellular()) {
      return;
    }

    // Only show syncing banner if there is actually something to upload
    const { vc, cc, gc } = await refreshCounts();
    if (vc === 0 && cc === 0 && gc === 0) return;

    syncingRef.current = true;
    setSyncing(true);

    try {
      // ── Sync catches ────────────────────────────────────────────────────
      const catches = await getAllPendingCatches();
      for (const record of catches) {
        try {
          const { userId, sessionId, catchData, conditionsSnapshot, location, tournament } = record;
          if (!userId || !sessionId) {
            await removePendingCatch(record.id);
            continue;
          }
          const catchRef = doc(collection(db, "users", userId, "sessions", sessionId, "catches"));
          await authedWrite(() => setDoc(catchRef, {
            sessionId,
            caughtAt: serverTimestamp(),
            outcome: catchData.outcome === "missed" ? "missed" : "landed",
            species: catchData.species ?? null,
            lengthIn: catchData.lengthIn ?? null,
            weightLbs: catchData.weightLbs ?? null,
            lure: catchData.lure ?? null,
            waterClarity: catchData.waterClarity ?? null,
            transcript: catchData.transcript ?? null,
            location: location ? { lat: location.lat, lng: location.lng } : null,
            conditionsSnapshot: conditionsSnapshot
              ? { ...conditionsSnapshot, capturedAt: serverTimestamp() }
              : null,
            // Tournament fields so the director dashboard can discover the catch.
            tournamentId: tournament?.tournamentId ?? catchData.tournamentId ?? null,
            tournamentName: tournament?.tournamentName ?? catchData.tournamentName ?? null,
            teamId: tournament?.teamId ?? catchData.teamId ?? null,
            teamName: tournament?.teamName ?? catchData.teamName ?? null,
            reviewStatus: "pending",
            syncedFromOffline: true,
          }));
          await removePendingCatch(record.id);
          // Catch counts changed — refresh the dashboard's pattern-tier pill.
          invalidatePatternTierCache(userId);
        } catch (err) {
          console.warn("[OfflineSync] catch sync failed, will retry:", err);
        }
      }

      // ── Sync videos ─────────────────────────────────────────────────────
      const videos = await getAllPendingVideos();
      for (const record of videos) {
        const itemKey = `video:${record.id}`;
        if ((attemptsRef.current.get(itemKey) || 0) >= MAX_ATTEMPTS) {
          // Poison item — skip so it doesn't block the rest of the queue.
          continue;
        }
        try {
          const { certId, blob: recordBlob, metadata } = record;
          // Large clips are staged on disk (native): read a disk-backed Blob
          // that r2Video can slice() for multipart without heap materialization.
          let blob = recordBlob;
          const stagedPath = record.filePath || null;
          if (!blob && stagedPath) {
            blob = await getFileBlob({ path: stagedPath });
          }
          if (!blob) {
            console.warn("[OfflineSync] pending video has no blob or staged file; skipping:", certId);
            continue;
          }
          const uploaderUid =
            metadata?.videoMeta?.userId ||
            metadata?.userId ||
            null;
          if (!uploaderUid) {
            console.warn("[OfflineSync] pending video missing userId; skipping:", certId);
            continue;
          }
          const storagePath = `videos/${uploaderUid}/${certId}.webm`;
          const ref = storageRef(storage, storagePath);
          const safeCustomMetadata = {
            certId: String(metadata?.certId || certId),
            hash: String(metadata?.hash || ""),
            timestamp: String(metadata?.timestamp || ""),
            syncedFromOffline: "true",
          };

          const useR2 = isR2Backend();
          let resolvedStoragePath = storagePath;
          if (useR2) {
            // Cloudflare R2: presigned PUT (multipart for large clips). Key is
            // derived server-side from uid + certId + content type.
            const { key } = await r2UploadVideo({
              certId,
              blob,
              contentType: blob?.type || "video/webm",
              source: metadata?.videoMeta?.source || metadata?.source || "live",
              onProgress: (loaded, total) => {
                setActiveUpload({
                  key: itemKey,
                  type: "video",
                  pct: total ? Math.round((loaded / total) * 100) : 0,
                });
              },
            });
            resolvedStoragePath = key;
          } else {
            const uploadTask = uploadBytesResumable(ref, blob, {
              contentType: "video/webm",
              customMetadata: safeCustomMetadata,
            });

            await new Promise((resolve, reject) => {
              uploadTask.on(
                "state_changed",
                (snap) => {
                  setActiveUpload({
                    key: itemKey,
                    type: "video",
                    pct: snap.totalBytes
                      ? Math.round((snap.bytesTransferred / snap.totalBytes) * 100)
                      : 0,
                  });
                },
                reject,
                resolve
              );
            });
          }

          // Success — clear any prior attempt count for this item.
          attemptsRef.current.delete(itemKey);
          setActiveUpload(null);

          // Remove from queue immediately after successful upload so a Firestore
          // failure doesn't cause the video to be re-uploaded next time
          await removePendingVideo(record.id);

          // Free the staged file from the device (delete-after-upload).
          if (stagedPath && settingsRef.current.deleteAfterUpload !== false) {
            await deleteStagedFile({ path: stagedPath });
          }

          // Best-effort Firestore metadata — does not block queue removal
          try {
            let url = "";
            try {
              url = (useR2
                ? await r2GetPlaybackUrl(resolvedStoragePath)
                : await getDownloadURL(ref)) || "";
            } catch (urlErr) {
              console.warn("[OfflineSync] failed to mint playback URL (non-fatal; readers resolve from storagePath):", urlErr);
            }
            // Phase 7: on R2 the presigned GET is short-lived and must NOT be
            // persisted as a durable field. Readers re-mint from storagePath.
            const durableUrl = useR2 ? "" : url;
            const certificateMeta = metadata?.certificateMeta || metadata?.firestoreMeta || {};
            await authedWrite(() => setDoc(
              doc(db, "certificates", certId),
              {
                certId,
                storagePath: resolvedStoragePath,
                downloadUrl: durableUrl,
                syncedFromOffline: true,
                syncedAt: serverTimestamp(),
                ...certificateMeta,
              },
              { merge: true }
            ));

            if (metadata?.videoMeta) {
              await authedWrite(() => setDoc(
                doc(db, "videos", certId),
                {
                  certId,
                  storagePath: resolvedStoragePath,
                  downloadUrl: durableUrl,
                  syncedFromOffline: true,
                  syncedAt: serverTimestamp(),
                  ...metadata.videoMeta,
                },
                { merge: true }
              ));
            }

            if (metadata?.directorEventMeta) {
              await sendDirectorCertifiedVideoEvent({
                ...metadata.directorEventMeta,
                downloadUrl: url,
                source: "offline-sync-upload",
              });
            }
          } catch (metaErr) {
            console.warn("[OfflineSync] Firestore metadata write failed (non-fatal):", metaErr);
          }
        } catch (err) {
          console.warn("[OfflineSync] video sync failed, will retry:", err);
          const itemKeyForFail = `video:${record.id}`;
          attemptsRef.current.set(itemKeyForFail, (attemptsRef.current.get(itemKeyForFail) || 0) + 1);
          setActiveUpload(null);
        }
      }

      // ── Sync geofence alerts ────────────────────────────────────────────
      const geofenceAlerts = await getAllPendingGeofenceAlerts();
      for (const record of geofenceAlerts) {
        try {
          await sendTournamentGeofenceAlert({
            eventId: record.eventId,
            tournamentId: record.tournamentId,
            tournamentName: record.tournamentName,
            teamId: record.teamId,
            teamName: record.teamName,
            userId: record.userId,
            sessionId: record.sessionId,
            location: record.location,
            source: "offline-sync",
          });
          await removePendingGeofenceAlert(record.id);
        } catch (err) {
          console.warn("[OfflineSync] geofence alert sync failed, will retry:", err);
        }
      }

      setLastSyncAt(Date.now());
    } catch (err) {
      console.error("[OfflineSync] sync error:", err);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
      refreshCounts();
    }
  }, [refreshCounts]);

  // Refresh counts on mount only
  useEffect(() => {
    refreshCounts();
  }, [refreshCounts]);

  // Re-read the queue whenever an item is added or removed (e.g. a video is
  // certified while offline), so counts/items/badge update immediately.
  useEffect(() => {
    const handler = () => refreshCounts();
    window.addEventListener(QUEUE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(QUEUE_CHANGED_EVENT, handler);
  }, [refreshCounts]);

  // Delete a single queued item from the device (and its staged file).
  const removeItem = useCallback(async (item) => {
    if (!item) return;
    try {
      if (item.type === "video") {
        await removePendingVideo(item.id);
        if (item.filePath) await deleteStagedFile({ path: item.filePath });
      } else if (item.type === "catch") {
        await removePendingCatch(item.id);
      } else if (item.type === "alert") {
        await removePendingGeofenceAlert(item.id);
      }
    } catch (err) {
      console.warn("[OfflineSync] removeItem failed:", err);
    }
    await refreshCounts();
  }, [refreshCounts]);

  // Only trigger sync on the offline → online transition, not on every render
  useEffect(() => {
    const wasOffline = prevIsOfflineRef.current;
    prevIsOfflineRef.current = isOffline;
    if (wasOffline && !isOffline && settingsRef.current.autoUpload !== false) {
      syncAll(false);
    }
  }, [isOffline, syncAll]);

  return {
    pendingVideoCount,
    pendingCatchCount,
    pendingGeofenceAlertCount,
    syncing,
    lastSyncAt,
    items,
    activeUpload,
    refreshCounts,
    removeItem,
    retryNow: () => syncAll(true),
  };
}
